import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as userService from '../services/userService';
import * as roleService from '../services/roleService';
import { sprayService, SprayOperator } from '../services/sprayService';
import { DataTable, Field, FormSection, Modal, Notice, StatusPill } from './patterns';
import type { DataTableColumn } from './patterns';

const UserManagement: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [operators, setOperators] = useState<SprayOperator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastCreated, setLastCreated] = useState<{ name: string; tempPassword: string } | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    name: '',
    role: '',
    active: true
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersData, rolesData] = await Promise.all([
        userService.listUsers(),
        roleService.listRoles()
      ]);
      setUsers(usersData);
      setRoles(rolesData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load user data');
    } finally {
      setLoading(false);
    }
    // Best-effort: a user-management admin may not also hold Spray Records
    // read permission. If the operators fetch fails, the linked-operator
    // echo line in the Edit User modal silently disappears, which is the
    // correct fallback (no info > stale info).
    try {
      const list = await sprayService.getOperators();
      setOperators(list || []);
    } catch {
      setOperators([]);
    }
  };

  /**
   * Map of userId → linked SprayOperator. Built once per operators-list
   * change so the per-row lookup in the Edit modal is O(1).
   * Track: spray_session_workflow_20260427 / Phase 3 / Task 8.
   */
  const operatorByUserId = useMemo(() => {
    const map = new Map<string, SprayOperator>();
    for (const op of operators) {
      if (op.userId) map.set(String(op.userId), op);
    }
    return map;
  }, [operators]);

  const linkedOperator = currentUser ? operatorByUserId.get(currentUser._id) : undefined;

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenModal = (user: any = null) => {
    if (user) {
      setCurrentUser(user);
      setFormData({
        username: user.username,
        email: user.email || '',
        password: '', // Don't show password
        name: user.name,
        role: user.role?._id || '',
        active: user.active
      });
    } else {
      setCurrentUser(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        name: '',
        role: roles.length > 0 ? roles[0]._id : '',
        active: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (currentUser) {
        // Only send password if changed
        const updatePayload = { ...formData };
        if (!updatePayload.password) delete (updatePayload as any).password;
        await userService.updateUser(currentUser._id, updatePayload);
        handleCloseModal();
        fetchData();
      } else {
        // Backend auto-generates the temp password; password field in formData is not used.
        const result = await userService.createUser(formData);
        handleCloseModal();
        fetchData();
        setLastCreated({ name: result.user.name, tempPassword: result.tempPassword });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save user');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('userManagement.deleteConfirm'))) {
      try {
        await userService.deleteUser(id);
        fetchData();
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to delete user');
      }
    }
  };

  const columns: DataTableColumn<any>[] = [
    {
      key: 'name',
      label: t('userManagement.nameHeader'),
      render: (user) => user.name,
    },
    {
      key: 'username',
      label: t('userManagement.usernameHeader'),
      render: (user) => user.username,
    },
    {
      key: 'email',
      label: t('userManagement.emailHeader'),
      render: (user) =>
        user.email || <span className="text-muted small">N/A</span>,
    },
    {
      key: 'role',
      label: t('userManagement.roleHeader'),
      render: (user) =>
        user.role?.name || (
          <span className="text-danger small">{t('userManagement.noRole')}</span>
        ),
    },
    {
      key: 'status',
      label: t('userManagement.statusHeader'),
      render: (user) => (
        <StatusPill variant={user.active ? 'ok' : 'info'}>
          {user.active ? t('userManagement.active') : t('userManagement.inactive')}
        </StatusPill>
      ),
    },
    {
      key: 'actions',
      label: t('userManagement.actionsHeader'),
      render: (user) => (
        <div className="user-management__row-actions">
          <button
            type="button"
            className="pat-btn pat-btn--outline"
            onClick={() => handleOpenModal(user)}
            aria-label="Edit"
          >
            <i className="bi bi-pencil" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="pat-btn pat-btn--danger"
            onClick={() => handleDelete(user._id)}
            disabled={user.username === 'admin'}
            aria-label="Delete"
          >
            <i className="bi bi-trash" aria-hidden="true" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="user-management">
      <div className="user-management__header">
        <h2 className="user-management__heading">{t('userManagement.heading')}</h2>
        <button
          type="button"
          className="pat-btn pat-btn--primary"
          onClick={() => handleOpenModal()}
        >
          <i className="bi bi-person-plus" aria-hidden="true" /> {t('userManagement.newUser')}
        </button>
      </div>

      {error && (
        <Notice tone="danger" action={<button type="button" className="user-management__alert-close" aria-label="Dismiss" onClick={() => setError(null)}>×</button>}>
          <span>{error}</span>
        </Notice>
      )}

      {lastCreated && (
        <Notice tone="success" className="mb-3">
            <strong>{t('userManagement.tempPasswordCreated', { name: lastCreated.name })}</strong>{' '}
            <code style={{ fontFamily: 'monospace', fontSize: 16, padding: '4px 8px', background: 'rgba(0,0,0,0.05)', borderRadius: 4 }}>
              {lastCreated.tempPassword}
            </code>{' '}
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={() => { navigator.clipboard.writeText(lastCreated.tempPassword); }}
            >
              {t('userManagement.copyTempPassword')}
            </button>
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={() => setLastCreated(null)}
              style={{ marginLeft: 8 }}
            >
              {t('common.dismiss')}
            </button>
        </Notice>
      )}

      {loading ? (
        <p>{t('userManagement.loading')}</p>
      ) : (
        <DataTable<any>
          columns={columns}
          rows={users}
          rowKey={(user) => user._id}
        />
      )}

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        title={currentUser ? t('userManagement.editTitle') : t('userManagement.createTitle')}
      >
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            <FormSection id="user-form" title={currentUser ? t('userManagement.editTitle') : t('userManagement.createTitle')}>
              <Field label={t('userManagement.fullName')} controlId="user-name" required>
                <input
                  id="user-name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('userManagement.fullNamePlaceholder')}
                />
              </Field>

              <Field label={t('userManagement.username')} controlId="user-username" required>
                <input
                  id="user-username"
                  type="text"
                  required
                  value={formData.username}
                  onChange={e => setFormData({ ...formData, username: e.target.value })}
                  placeholder={t('userManagement.usernamePlaceholder')}
                />
              </Field>

              <Field label={t('userManagement.email')} controlId="user-email">
                <input
                  id="user-email"
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  placeholder={t('userManagement.emailPlaceholder')}
                />
              </Field>

              {currentUser ? (
                <Field
                  label={t('userManagement.newPassword')}
                  controlId="user-password"
                >
                  <input
                    id="user-password"
                    type="password"
                    value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                  />
                </Field>
              ) : (
                <p className="user-management__temp-password-help" style={{ fontSize: '0.875rem', color: 'var(--pat-color-text-secondary, #6c757d)', marginBottom: 8 }}>
                  {t('userManagement.tempPasswordHelp')}
                </p>
              )}

              <Field
                label={t('userManagement.role')}
                controlId="user-role"
                required
                helpText={
                  currentUser?.role?.isFarmAdministrator
                    ? t('userManagement.systemRoleHelp')
                    : undefined
                }
              >
                <select
                  id="user-role"
                  required
                  value={formData.role}
                  onChange={e => setFormData({ ...formData, role: e.target.value })}
                  disabled={currentUser?.role?.isFarmAdministrator}
                >
                  <option value="" disabled>{t('userManagement.selectRole')}</option>
                  {roles.map(role => (
                    <option key={role._id} value={role._id}>{role.name}</option>
                  ))}
                </select>
              </Field>

              <label className="user-management__switch-row">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={e => setFormData({ ...formData, active: e.target.checked })}
                />
                <span>{t('userManagement.accountActive')}</span>
              </label>

              {linkedOperator && (
                <div className="user-management__linked-operator" data-testid="linked-spray-operator">
                  {t('userManagement.linkedSprayOperator')}:{' '}
                  <strong>{linkedOperator.name}</strong>{' '}
                  <Link to="/protection/operators">
                    {t('userManagement.manageInOperators')}
                  </Link>
                </div>
              )}
            </FormSection>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={handleCloseModal}
            >
              {t('userManagement.cancel')}
            </button>
            <button type="submit" className="pat-btn pat-btn--primary">
              {t('userManagement.saveUser')}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default UserManagement;
